import { createContext, Dispatch, FC, SetStateAction, useContext, useState, useEffect } from 'react';
import { Toaster } from 'react-hot-toast';
import { useLockBodyScroll } from 'react-use';
import { useIsLargeViewport } from '../hooks';

interface ElementsUIContextProps {
  children?: React.ReactNode;
}

interface IElementsUIContext extends Omit<ElementsUIContextProps, 'children'> {
  sideOverOpen: boolean;
  bodyScrollLocked: boolean;
  bodyScrollLockerID: string | undefined;
  setSideOverOpen: Dispatch<SetStateAction<boolean>>;
  setBodyScrollLocked: React.Dispatch<React.SetStateAction<boolean>>;
  setBodyScrollLockerID: React.Dispatch<React.SetStateAction<string | undefined>>;
  isAsideOpen: boolean;
  setIsAsideOpen: Dispatch<SetStateAction<boolean>>;
  hideDashboardFAB: boolean;
  setHideDashboardFAB: Dispatch<SetStateAction<boolean>>;
  hideDashboardNav: boolean;
  setHideDashboardNav: Dispatch<SetStateAction<boolean>>;
  isNavigationBarOpen: boolean;
  setIsNavigationBarOpen: Dispatch<SetStateAction<boolean>>;
}

const ElementsUIContext = createContext<IElementsUIContext | null>(null);

export const useElementsUIContext = () => {
  const context = useContext(ElementsUIContext);

  if (!context) {
    throw new Error('Make sure to use the ElementsUIContext inside of the provider');
  }

  return context;
};

export const ElementsUIContextProvider: FC<ElementsUIContextProps> = ({ children }) => {
  const isLargeScreen = useIsLargeViewport();

  const [isAsideOpen, setIsAsideOpen] = useState(false);
  const [sideOverOpen, setSideOverOpen] = useState(false);
  const [bodyScrollLocked, setBodyScrollLocked] = useState(false);
  const [hideDashboardFAB, setHideDashboardFAB] = useState(false);
  const [hideDashboardNav, setHideDashboardNav] = useState(false);
  const [isNavigationBarOpen, setIsNavigationBarOpen] = useState(false);
  const [bodyScrollLockerID, setBodyScrollLockerID] = useState<string>();
  
  useLockBodyScroll(bodyScrollLocked);
  
  useEffect(() => {
    setIsAsideOpen(isLargeScreen);
    setIsNavigationBarOpen(isLargeScreen);
  }, [isLargeScreen]);


  return (
    <ElementsUIContext.Provider
      value={{
        sideOverOpen,
        setSideOverOpen,
        bodyScrollLocked,
        bodyScrollLockerID,
        setBodyScrollLocked,
        setBodyScrollLockerID,
        isAsideOpen,
        setIsAsideOpen,
        hideDashboardFAB,
        setHideDashboardFAB,
        hideDashboardNav,
        setHideDashboardNav,
        isNavigationBarOpen,
        setIsNavigationBarOpen,
      }}
    >
      <Toaster />
      {children}
    </ElementsUIContext.Provider>
  );
};
